import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react'
import Overlay from 'react-bootstrap/Overlay'
import BSTooltip from 'react-bootstrap/Tooltip' // Renamed to avoid conflict with our Tooltip component

// --- Context Definition ---
const TooltipContext = createContext(undefined)

export const useTooltips = () => {
  const context = useContext(TooltipContext)
  if (!context) {
    throw new Error('useTooltips must be used within a TooltipProvider')
  }
  return context
}

// --- Tooltip Provider Component ---
export const TooltipProvider = ({ children }) => {
  const [tooltipConfig, setTooltipConfig] = useState({
    show: false,
    content: {}, // { sectionName, rowName, seatName, type: 'section' | 'row' | 'seat' }
    position: { x: 0, y: 0 }, // To position the tooltip near the mouse
    target: null, // A dummy target for Overlay, as we'll position with style
  })
  const dummyTargetRef = useRef(null) // Ref for the Overlay target

  const showTooltip = useCallback((content, event) => {
    // Use a dummy div for the Overlay target, and position based on mouse
    // This ensures the Overlay has a target to attach to.
    // We'll then use styling (left, top) to position it precisely by the cursor.
    if (!dummyTargetRef.current) {
      const dummy = document.createElement('div')
      document.body.appendChild(dummy)
      dummyTargetRef.current = dummy
    }

    setTooltipConfig({
      show: true,
      content,
      // Get mouse position from the event
      position: { x: event.evt.clientX, y: event.evt.clientY },
      target: dummyTargetRef.current,
    })
  }, [])

  const hideTooltip = useCallback(() => {
    setTooltipConfig((prev) => ({ ...prev, show: false }))
  }, [])

  const value = {
    showTooltip,
    hideTooltip,
    tooltipConfig,
  }

  return (
    <TooltipContext.Provider value={value}>
      {children}
      <Tooltip />
    </TooltipContext.Provider>
  )
}

function Tooltip() {
  const { tooltipConfig } = useTooltips()
  const { show, content, position, target } = tooltipConfig

  if (!show || !target) {
    return null
  }

  let tooltipContentParts = []

  // Helper function to create a styled part of the tooltip
  const createContentPart = (label, value) => (
    <span key={label} style={{ display: 'flex', alignItems: 'center' }}>
      {label && <span style={{ marginRight: '0.25em' }}>{label}:</span>}
      <strong style={{ fontSize: '0.9rem' }}>{value}</strong>
    </span>
  )

  if (content.type === 'section') {
    tooltipContentParts.push(
      createContentPart('SECTION', content.sectionNumber)
    )
  } else if (content.type === 'row') {
    tooltipContentParts.push(createContentPart('ROW', content.rowNumber))
    tooltipContentParts.push(createContentPart('SEC', content.sectionNumber))
  } else if (content.type === 'seat') {
    tooltipContentParts.push(createContentPart('SEAT', content.seatNumber))
    tooltipContentParts.push(createContentPart('ROW', content.rowNumber))
    tooltipContentParts.push(createContentPart('SEC', content.sectionNumber))

    if (content.firstName) {
      tooltipContentParts.push(
        createContentPart('FIRST NAME', content.firstName)
      )
    }
    if (content.lastName) {
      tooltipContentParts.push(createContentPart('LAST NAME', content.lastName))
    }
    if (content.email) {
      tooltipContentParts.push(createContentPart('EMAIL', content.email))
    }
    if (content.holdName) {
      tooltipContentParts.push(createContentPart('HOLD', content.holdName))
    }
    if (content.additionalLabel) {
      tooltipContentParts.push(
        createContentPart('LABEL', content.additionalLabel)
      )
    }
  } else {
    tooltipContentParts.push(<span>Details unavailable</span>)
  }

  // Join the parts
  const assembledTooltipContent = tooltipContentParts.reduce(
    (acc, part, index) => {
      if (index === 0) {
        return [part]
      }
      return [...acc, part]
    },
    []
  )

  return (
    <Overlay
      show={show}
      target={target}
      placement="top" // This placement might be overridden by fixed positioning, but good to keep
    >
      {(props) => (
        <BSTooltip
          {...props}
          style={{
            ...props.style,
            position: 'fixed',
            left: `${position.x + 15}px`,
            top: `${position.y + 15}px`,
            transform: 'none',
            fontSize: '0.75rem',
            maxWidth: 300,
          }}
        >
          {/* Wrap the content in a div that also uses flex for alignment between parts */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              width: '100%',
              gap: '0.25em',
            }}
          >
            {assembledTooltipContent}
          </div>
        </BSTooltip>
      )}
    </Overlay>
  )
}
